import React from 'react';
import { Card } from 'antd';
import { SkBar } from './_primitives';

const TiersHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <SkBar h="h-7" w="w-64" className="max-w-full" />
      <SkBar h="h-3" w="w-80" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-32" className="rounded-md flex-shrink-0" />
  </div>
);

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-7" w="w-20" />
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    </div>
  </Card>
);

const TierLadderTile = () => (
  <div className="p-4 border border-gray-200 rounded-lg flex flex-col items-center gap-3">
    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
    <SkBar h="h-4" w="w-20" />
    <SkBar h="h-3" w="w-24" />
    <SkBar h="h-5" w="w-8" />
    <SkBar h="h-2" w="w-16" />
    <SkBar h="h-2" w="w-full" className="rounded-full" />
    <SkBar h="h-3" w="w-24" />
  </div>
);

const TIER_COLS = ['w-10', 'w-24', 'w-28', 'w-16', 'w-12', 'w-14'];

const TierRow = () => (
  <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_1fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Tier: icon + name + level */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-20" />
        <SkBar h="h-2" w="w-12" />
      </div>
    </div>
    {/* Points threshold */}
    <SkBar h="h-3" w="w-16" />
    {/* Completion bonus pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md" />
    {/* Members */}
    <SkBar h="h-3" w="w-8" />
    {/* Status badge */}
    <SkBar h="h-5" w="w-14" className="rounded-md" />
    {/* Action icons */}
    <div className="flex items-center gap-2 justify-end">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
  </div>
);

/**
 * TiersPageLoader — matches Member Tier Management layout:
 *  header (title/subtitle · add button) · 4 stat cards (icon right) ·
 *  tier progression ladder (4 tiles) · tier configuration table.
 */
const TiersPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <TiersHeader />

    {/* 4 stat cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    {/* Tier Progression Ladder */}
    <Card className="shadow-sm mb-4 sm:mb-6" styles={{ body: { padding: 20 } }}>
      <SkBar h="h-4" w="w-72" className="max-w-full mb-4" />
      <div className="flex flex-wrap lg:flex-nowrap gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex-1 min-w-[200px]">
            <TierLadderTile />
          </div>
        ))}
      </div>
    </Card>

    {/* Tier Configuration & Benefits table */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <SkBar h="h-4" w="w-56" className="max-w-full mb-4" />
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* Header row */}
          <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_1fr_1fr] gap-3 pb-3 border-b border-gray-200">
            {TIER_COLS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === TIER_COLS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 4 }).map((_, i) => (
            <TierRow key={i} />
          ))}
        </div>
      </div>
    </Card>
  </div>
);

export default TiersPageLoader;
